// =============================================================================
// MeuDriver — Batch R2: InstructorVehicles
// Gerenciamento de frota de veículos do instrutor — /instrutor/veiculos
// =============================================================================

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Car,
  Plus,
  Pencil,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Loader2,
  AlertCircle,
} from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { useQuery } from '@tanstack/react-query'
import {
  useInstructorVehicles,
  useCreateVehicle,
  useUpdateVehicle,
  useDeleteVehicle,
} from '@/hooks/useInstructorVehicles'
import type { InstructorVehicleRow } from '@/types/database'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'

// ---------------------------------------------------------------------------
// Tipos de veículo suportados
// ---------------------------------------------------------------------------

const VEHICLE_TYPES = [
  'Carro Manual',
  'Carro Automático',
  'Moto',
  'Caminhão',
  'Ônibus',
  'Outro',
] as const

// ---------------------------------------------------------------------------
// Validação do formulário
// ---------------------------------------------------------------------------

const vehicleSchema = z.object({
  make:  z.string().min(1, 'Marca é obrigatória').max(100),
  model: z.string().min(1, 'Modelo é obrigatório').max(100),
  year:  z.coerce.number()
    .int()
    .min(1950, 'Ano mínimo: 1950')
    .max(2030, 'Ano máximo: 2030')
    .optional(),
  plate: z.string().max(20).optional().or(z.literal('')),
  type:  z.string().optional(),
})

type VehicleFormData = z.infer<typeof vehicleSchema>

// ---------------------------------------------------------------------------
// Modal de criar/editar
// ---------------------------------------------------------------------------

interface VehicleModalProps {
  open: boolean
  onClose: () => void
  instructorId: string
  vehicle?: InstructorVehicleRow
}

function VehicleModal({ open, onClose, instructorId, vehicle }: VehicleModalProps) {
  const create = useCreateVehicle()
  const update = useUpdateVehicle()
  const isEditing = !!vehicle

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<VehicleFormData>({
    resolver: zodResolver(vehicleSchema),
    defaultValues: {
      make:  vehicle?.make  ?? '',
      model: vehicle?.model ?? '',
      year:  vehicle?.year  ?? undefined,
      plate: vehicle?.plate ?? '',
      type:  vehicle?.type  ?? '',
    },
  })

  const selectedType = watch('type')

  async function onSubmit(data: VehicleFormData) {
    try {
      if (isEditing && vehicle) {
        await update.mutateAsync({
          id:           vehicle.id,
          instructorId: vehicle.instructor_id,
          make:         data.make,
          model:        data.model,
          year:         data.year ?? null,
          plate:        data.plate || null,
          type:         data.type || null,
        })
        toast.success('Veículo atualizado.')
      } else {
        await create.mutateAsync({
          instructor_id: instructorId,
          make:          data.make,
          model:         data.model,
          year:          data.year ?? null,
          plate:         data.plate || null,
          type:          data.type || null,
        })
        toast.success('Veículo adicionado.')
      }
      reset()
      onClose()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao salvar veículo.'
      toast.error(msg)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose() } }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Car className="h-4 w-4" />
            {isEditing ? 'Editar veículo' : 'Adicionar veículo'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="make">Marca *</Label>
              <Input
                id="make"
                placeholder="Ex: Volkswagen"
                {...register('make')}
                className={errors.make ? 'border-red-400' : ''}
              />
              {errors.make && <p className="text-xs text-red-600">{errors.make.message}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="model">Modelo *</Label>
              <Input
                id="model"
                placeholder="Ex: Gol"
                {...register('model')}
                className={errors.model ? 'border-red-400' : ''}
              />
              {errors.model && <p className="text-xs text-red-600">{errors.model.message}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="year">Ano</Label>
              <Input
                id="year"
                type="number"
                placeholder="Ex: 2020"
                {...register('year')}
                className={errors.year ? 'border-red-400' : ''}
              />
              {errors.year && <p className="text-xs text-red-600">{errors.year.message}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="plate">Placa</Label>
              <Input
                id="plate"
                placeholder="Ex: ABC-1234"
                {...register('plate')}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Tipo de veículo</Label>
            <Select
              value={selectedType ?? ''}
              onValueChange={(val) => setValue('type', val)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione o tipo..." />
              </SelectTrigger>
              <SelectContent>
                {VEHICLE_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <Button type="button" variant="outline" onClick={() => { reset(); onClose() }}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting} className="gap-2">
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEditing ? 'Salvar alterações' : 'Adicionar'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Card de veículo
// ---------------------------------------------------------------------------

interface VehicleCardProps {
  vehicle: InstructorVehicleRow
  onEdit: (v: InstructorVehicleRow) => void
  onDelete: (v: InstructorVehicleRow) => void
  onToggle: (v: InstructorVehicleRow) => void
}

function VehicleCard({ vehicle, onEdit, onDelete, onToggle }: VehicleCardProps) {
  return (
    <div
      data-testid="vehicle-card"
      className={`rounded-xl border p-4 transition-all ${
        vehicle.is_active
          ? 'border-gray-200 bg-white'
          : 'border-gray-100 bg-gray-50 opacity-60'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-100">
            <Car className="h-5 w-5 text-brand-600" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-gray-900 truncate">
              {vehicle.make} {vehicle.model}
            </p>
            <div className="flex items-center flex-wrap gap-1.5 mt-0.5">
              {vehicle.year && (
                <span className="text-xs text-muted-foreground">{vehicle.year}</span>
              )}
              {vehicle.plate && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-mono">
                  {vehicle.plate}
                </Badge>
              )}
              {vehicle.type && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                  {vehicle.type}
                </Badge>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-muted-foreground hover:text-brand-600"
            onClick={() => onToggle(vehicle)}
            title={vehicle.is_active ? 'Desativar' : 'Ativar'}
            data-testid="toggle-vehicle-btn"
          >
            {vehicle.is_active
              ? <ToggleRight className="h-4 w-4 text-green-500" />
              : <ToggleLeft className="h-4 w-4" />
            }
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-muted-foreground hover:text-brand-600"
            onClick={() => onEdit(vehicle)}
            data-testid="edit-vehicle-btn"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-muted-foreground hover:text-red-600"
            onClick={() => onDelete(vehicle)}
            data-testid="delete-vehicle-btn"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Página principal
// ---------------------------------------------------------------------------

export default function InstructorVehicles() {
  const { user } = useAuth()
  const [modalOpen, setModalOpen] = useState(false)
  const [editingVehicle, setEditingVehicle] = useState<InstructorVehicleRow | undefined>()

  // Busca o instructor_id a partir do profile_id do usuário autenticado
  const { data: instructorData } = useQuery<{ id: string } | null>({
    queryKey: ['instructor_id_by_profile', user?.id],
    enabled: !!user?.id,
    staleTime: Infinity,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('instructors')
        .select('id')
        .eq('profile_id', user!.id)
        .single()
      if (error) return null
      return data as { id: string }
    },
  })

  const instructorId = instructorData?.id ?? ''

  const { data: vehicles = [], isLoading } = useInstructorVehicles(instructorId)
  const updateVehicle = useUpdateVehicle()
  const deleteVehicle = useDeleteVehicle()

  function handleEdit(v: InstructorVehicleRow) {
    setEditingVehicle(v)
    setModalOpen(true)
  }

  function handleAdd() {
    setEditingVehicle(undefined)
    setModalOpen(true)
  }

  function handleCloseModal() {
    setModalOpen(false)
    setEditingVehicle(undefined)
  }

  async function handleToggle(v: InstructorVehicleRow) {
    try {
      await updateVehicle.mutateAsync({
        id:           v.id,
        instructorId: v.instructor_id,
        is_active:    !v.is_active,
      })
      toast.success(v.is_active ? 'Veículo desativado.' : 'Veículo ativado.')
    } catch {
      toast.error('Erro ao alterar status do veículo.')
    }
  }

  async function handleDelete(v: InstructorVehicleRow) {
    if (!confirm(`Excluir o veículo "${v.make} ${v.model}"? Esta ação não pode ser desfeita.`)) return
    try {
      await deleteVehicle.mutateAsync({ id: v.id, instructorId: v.instructor_id })
      toast.success('Veículo removido.')
    } catch {
      toast.error('Erro ao remover veículo.')
    }
  }

  if (!instructorId && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
        <AlertCircle className="h-8 w-8 text-muted-foreground opacity-30" />
        <p className="text-sm text-muted-foreground">Perfil de instrutor não encontrado.</p>
      </div>
    )
  }

  const active = vehicles.filter((v) => v.is_active)
  const inactive = vehicles.filter((v) => !v.is_active)

  return (
    <div className="max-w-2xl mx-auto space-y-6">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Car className="h-6 w-6 text-brand-600" />
            Meus veículos
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Gerencie os veículos disponíveis para suas aulas.
          </p>
        </div>
        <Button
          onClick={handleAdd}
          disabled={!instructorId}
          className="gap-2 shrink-0"
          data-testid="add-vehicle-btn"
        >
          <Plus className="h-4 w-4" />
          Adicionar
        </Button>
      </div>

      {/* Lista */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
      ) : vehicles.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 gap-3 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
              <Car className="h-7 w-7 text-gray-300" />
            </div>
            <p className="font-medium text-gray-700">Nenhum veículo cadastrado</p>
            <p className="text-sm text-muted-foreground">
              Adicione os veículos que você usa nas aulas para que os alunos saibam o que esperar.
            </p>
            <Button onClick={handleAdd} className="mt-2 gap-2">
              <Plus className="h-4 w-4" />
              Adicionar primeiro veículo
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Ativos */}
          {active.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-gray-700 flex items-center gap-2">
                  Veículos ativos
                  <Badge className="ml-1">{active.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {active.map((v) => (
                  <VehicleCard
                    key={v.id}
                    vehicle={v}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggle={handleToggle}
                  />
                ))}
              </CardContent>
            </Card>
          )}

          {/* Inativos */}
          {inactive.length > 0 && (
            <>
              <Separator />
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base text-gray-400 flex items-center gap-2">
                    Inativos
                    <Badge variant="outline" className="ml-1">{inactive.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {inactive.map((v) => (
                    <VehicleCard
                      key={v.id}
                      vehicle={v}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onToggle={handleToggle}
                    />
                  ))}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

      {/* Modal */}
      {instructorId && (
        <VehicleModal
          open={modalOpen}
          onClose={handleCloseModal}
          instructorId={instructorId}
          vehicle={editingVehicle}
        />
      )}
    </div>
  )
}
